import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Images, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { thumbUrl } from '../api/client';
import { useCollections, useCreateCollection } from '../api/hooks';
import { Button, EmptyState, Field, Input, Modal, Spinner, TextArea } from '../components/ui';

export function CollectionsPage() {
  const { data, isLoading } = useCollections();
  const create = useCreateCollection();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const submit = async () => {
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ name: name.trim(), description });
      setName('');
      setDescription('');
      setOpen(false);
      toast.success('Album created');
    } catch (error) {
      toast.error('Could not create album', { description: (error as Error).message });
    }
  };

  const albums = data?.items ?? [];

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line bg-surface px-3 py-3 sm:px-4">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Albums</h1>
          <p className="mt-0.5 text-sm text-ink-dim">
            Named sets of files, gathered from anywhere and shared as one thing.
          </p>
        </div>
        <Button variant="primary" onClick={() => setOpen(true)}>
          <Plus className="size-4" aria-hidden />
          New album
        </Button>
      </div>

      <div className="flex-1 p-3 sm:p-4">
        {isLoading ? (
          <Spinner />
        ) : albums.length === 0 ? (
          <EmptyState
            icon={<Images className="size-10" />}
            title="No albums yet"
            body="An album can hold photos from any folder without moving them."
            action={
              <Button variant="primary" onClick={() => setOpen(true)}>
                <Plus className="size-4" aria-hidden />
                New album
              </Button>
            }
          />
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-5">
            {albums.map((album) => (
              <li key={album.id}>
                <Link
                  to="/collections/$collectionId"
                  params={{ collectionId: album.id }}
                  className="block overflow-hidden rounded-xl border border-line transition-colors hover:border-brand"
                >
                  <div className="flex aspect-[4/3] items-center justify-center bg-surface-2">
                    {album.coverFileId ? (
                      <img
                        src={thumbUrl(album.coverFileId, 480, 'fill')}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    ) : (
                      <Images className="size-8 text-ink-dim" aria-hidden />
                    )}
                  </div>
                  <div className="bg-surface px-3 py-2.5">
                    <p className="truncate font-medium">{album.name}</p>
                    <p className="flex items-center gap-1.5 truncate text-xs text-ink-dim">
                      {album.itemCount} item{album.itemCount === 1 ? '' : 's'}
                      {album.role !== 'owner' ? (
                        <>
                          <Users className="size-3" aria-hidden />
                          {album.ownerName}
                        </>
                      ) : null}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="New album"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={create.isPending} onClick={submit}>
              Create
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Summer 2026"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional"
          />
        </Field>
      </Modal>
    </div>
  );
}
